import { useRef, useEffect } from 'react'

const SEA = '#9FE1CB'
const LIME = '#C0DD97'
const LW = 8
const GLW = 16
const NR = 12
const GNR = 17

const W = 800
const H = 640
const CX = W / 2
const CY = H / 2
const R = 130

const NODES: Record<string, [number, number]> = {
  V0: [0, -1],
  V1: [0.866, -0.5],
  V2: [0.866, 0.5],
  V3: [0, 1],
  V4: [-0.866, 0.5],
  V5: [-0.866, -0.5],
  T_UL: [-1.7751, -1.0198],
  T_LL: [-1.7751, 1.0198],
  J_R: [1.7751, 0.9844],
  T_R1: [2.665, -0.3094],
  T_R2: [2.665, 0.4835]
}

interface Segment { f: string, t: string }

const CW_HEX: Segment[] = [
  { f: 'V0', t: 'V1' },
  { f: 'V1', t: 'V2' },
  { f: 'V2', t: 'V3' }
]
const CCW_HEX: Segment[] = [
  { f: 'V0', t: 'V5' },
  { f: 'V5', t: 'V4' },
  { f: 'V4', t: 'V3' }
]

const LEFT_ARMS: Segment[] = [
  { f: 'V5', t: 'T_UL' },
  { f: 'V4', t: 'T_LL' }
]
const RIGHT_ARM: Segment[] = [
  { f: 'V2', t: 'J_R' },
  { f: 'J_R', t: 'T_R2' },
  { f: 'T_R2', t: 'T_R1' }
]
const ALL_ARMS: Segment[] = [...LEFT_ARMS, ...RIGHT_ARM]
const HEX_NODES = ['V0', 'V1', 'V2', 'V3', 'V4', 'V5']
const ARM_NODES = ['T_UL', 'T_LL', 'J_R', 'T_R2', 'T_R1']

const T1 = 500
const T2 = 900
const T3 = 900
const T4 = 1100
const T5 = 600
const T6 = 650
const T7 = 300
const TT = T1 + T2 + T3 + T4 + T5 + T6 + T7

function cl (t: number): number { return Math.min(Math.max(t, 0), 1) }
function eo (t: number): number { return 1 - Math.pow(1 - cl(t), 3) }
function eio (t: number): number {
  const c = cl(t)
  return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2
}
function ei (t: number): number { const c = cl(t); return c * c * c }
function lp (a: number, b: number, t: number): number { return a + (b - a) * t }
function lpp (a: [number, number], b: [number, number], t: number): [number, number] {
  return [lp(a[0], b[0], t), lp(a[1], b[1], t)]
}

function res (k: string, cx: number, cy: number, s: number): [number, number] {
  const [nx, ny] = NODES[k]
  return [cx + nx * s, cy + ny * s]
}

function dn (c: CanvasRenderingContext2D, x: number, y: number, a: number, r: number): void {
  c.beginPath()
  c.arc(x, y, r, 0, Math.PI * 2)
  c.fillStyle = LIME
  c.globalAlpha = a
  c.fill()
  c.globalAlpha = 1
}

function db (
  c: CanvasRenderingContext2D,
  f: [number, number],
  t: [number, number],
  p: number,
  a: number,
  lw: number
): void {
  if (p <= 0) return
  const e = lpp(f, t, cl(p))
  c.beginPath()
  c.moveTo(f[0], f[1])
  c.lineTo(e[0], e[1])
  c.strokeStyle = SEA
  c.lineWidth = lw
  c.lineCap = 'round'
  c.lineJoin = 'round'
  c.globalAlpha = a
  c.stroke()
  c.globalAlpha = 1
}

function drawRing (
  c: CanvasRenderingContext2D, prog: number, alpha: number,
  cx: number, cy: number, s: number, lw: number, nr: number
): void {
  for (let i = 0; i < 3; i++) {
    const sp = cl(prog * 3 - i)
    if (sp <= 0) continue
    const cf = res(CW_HEX[i].f, cx, cy, s)
    const ct = res(CW_HEX[i].t, cx, cy, s)
    db(c, cf, ct, sp, alpha, lw)
    if (sp > 0.85) dn(c, ct[0], ct[1], alpha * cl((sp - 0.85) / 0.15), nr)
    const wf = res(CCW_HEX[i].f, cx, cy, s)
    const wt = res(CCW_HEX[i].t, cx, cy, s)
    db(c, wf, wt, sp, alpha, lw)
    if (sp > 0.85) dn(c, wt[0], wt[1], alpha * cl((sp - 0.85) / 0.15), nr)
  }
}

function drawFullRing (
  c: CanvasRenderingContext2D, alpha: number,
  cx: number, cy: number, s: number, lw: number, nr: number
): void {
  for (const seg of [...CW_HEX, ...CCW_HEX]) {
    const from = res(seg.f, cx, cy, s)
    const to = res(seg.t, cx, cy, s)
    db(c, from, to, 1, alpha, lw)
  }
  for (const k of HEX_NODES) {
    const [x, y] = res(k, cx, cy, s)
    dn(c, x, y, alpha, nr)
  }
}

function rf (
  c: CanvasRenderingContext2D, lt: number,
  cx: number, cy: number, s: number, lw: number, nr: number
): void {
  const p1 = T1
  const p2 = p1 + T2
  const p3 = p2 + T3
  const p4 = p3 + T4
  const p5 = p4 + T5
  const p6 = p5 + T6
  const v0 = res('V0', cx, cy, s)
  const v3 = res('V3', cx, cy, s)

  if (lt < p1) {
    const p = eo(lt / T1)
    dn(c, cx, lp(cy, v0[1], p), 1, nr)
    dn(c, cx, lp(cy, v3[1], p), 1, nr)
  } else if (lt < p2) {
    const p = eio((lt - p1) / T2)
    dn(c, v0[0], v0[1], 1, nr)
    drawRing(c, p, 1, cx, cy, s, lw, nr)
  } else if (lt < p3) {
    const p = eo((lt - p2) / T3)
    drawFullRing(c, 1, cx, cy, s, lw, nr)

    for (const arm of LEFT_ARMS) {
      const from = res(arm.f, cx, cy, s)
      const to = res(arm.t, cx, cy, s)
      db(c, from, to, p, 1, lw)
      if (p > 0.7) {
        const tp = lpp(from, to, p)
        dn(c, tp[0], tp[1], cl((p - 0.7) / 0.3), nr)
      }
    }

    for (let i = 0; i < RIGHT_ARM.length; i++) {
      const sp = cl(p * 3 - i)
      if (sp <= 0) continue
      const from = res(RIGHT_ARM[i].f, cx, cy, s)
      const to = res(RIGHT_ARM[i].t, cx, cy, s)
      db(c, from, to, sp, 1, lw)
      if (sp > 0.7) {
        const tp = lpp(from, to, sp)
        dn(c, tp[0], tp[1], cl((sp - 0.7) / 0.3), nr)
      }
    }
  } else if (lt < p4) {
    const p = (lt - p3) / T4
    const pulse = 0.75 + 0.25 * Math.sin(p * Math.PI * 2.5)
    drawFullRing(c, pulse, cx, cy, s, lw, nr)
    for (const arm of ALL_ARMS) {
      const from = res(arm.f, cx, cy, s)
      const to = res(arm.t, cx, cy, s)
      db(c, from, to, 1, pulse, lw)
    }
    for (const k of ARM_NODES) {
      const [x, y] = res(k, cx, cy, s)
      dn(c, x, y, pulse, nr)
    }
  } else if (lt < p5) {
    const p = ei((lt - p4) / T5)
    const aa = 1 - p
    drawFullRing(c, 1, cx, cy, s, lw, nr)
    for (const arm of ALL_ARMS) {
      const from = res(arm.f, cx, cy, s)
      const to = res(arm.t, cx, cy, s)
      db(c, from, to, 1, aa, lw)
    }
    for (const k of ARM_NODES) {
      const [x, y] = res(k, cx, cy, s)
      dn(c, x, y, aa, nr)
    }
  } else if (lt < p6) {
    const p = eio((lt - p5) / T6)
    const ss = s * (1 - p)
    const ra = 1 - p * 0.4
    if (ss > 0.5) drawFullRing(c, ra * (1 - p), cx, cy, ss, lw, nr)
    const tp = res('V0', cx, cy, ss)
    const bp = res('V3', cx, cy, ss)
    dn(c, tp[0], tp[1], 1, nr)
    dn(c, bp[0], bp[1], 1, nr)
  } else {
    dn(c, cx, cy, 1, nr)
    dn(c, cx, cy - 2, 1, nr)
  }
}

interface LoadingSpinnerProps {
  className?: string
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ className }) => {
  const glowRef = useRef<HTMLCanvasElement>(null)
  const mainRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const glowCanvas = glowRef.current
    const mainCanvas = mainRef.current
    if (glowCanvas == null || mainCanvas == null) return

    const gx = glowCanvas.getContext('2d')
    const mainCtx = mainCanvas.getContext('2d')
    if (gx == null || mainCtx == null) return

    let frameId: number
    const glowCtx = gx
    const fgCtx = mainCtx

    function draw (ts: number): void {
      const lt = ts % TT
      fgCtx.clearRect(0, 0, W, H)
      glowCtx.clearRect(0, 0, W, H)
      rf(glowCtx, lt, CX, CY, R, GLW, GNR)
      rf(fgCtx, lt, CX, CY, R, LW, NR)
      frameId = requestAnimationFrame(draw)
    }

    frameId = requestAnimationFrame(draw)
    return () => { cancelAnimationFrame(frameId) }
  }, [])

  return (
    <div
      data-testid='loading-spinner'
      className={`inline-block relative aspect-square ${className ?? 'w-16 h-16'}`}
      role='status'
    >
      <canvas
        ref={glowRef}
        width={W}
        height={H}
        className='absolute inset-0 w-full h-full'
        style={{ filter: 'blur(12px)' }}
      />
      <canvas
        ref={mainRef}
        width={W}
        height={H}
        className='absolute inset-0 w-full h-full'
      />
      <span className='sr-only'>Loading...</span>
    </div>
  )
}

export default LoadingSpinner
